/**
 * Local homebrew store (IndexedDB).
 * Replaces server homebrew API for local-first: all homebrew CRUD and list reads from here.
 */

import { apiClient } from '@/api/client';
import { normalizeLegacyHomebrewEntry, normalizeStructuredHomebrewData } from '@/utils/homebrewNormalization';

const DB_NAME = 'mythkit_homebrew';
const DB_VERSION = 1;
const STORE = 'entries';

let dbPromise = null;
let migrationAttempted = false;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not available'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('updated_at', 'updated_at', { unique: false });
      }
    };
  });
  return dbPromise;
}

function generateId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `hb_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * @typedef {Object} HomebrewEntry
 * @property {string} id
 * @property {string} name
 * @property {string} type - monster | spell | item | race | class | background | feat | subclass
 * @property {object} data
 * @property {string[]} [tags]
 * @property {string} [source]
 * @property {number} [version]
 * @property {string} [visibility]
 * @property {number|null} [campaign_id]
 * @property {string} [created_at]
 * @property {string} [updated_at]
 */

/**
 * Get all homebrew entries.
 * @returns {Promise<HomebrewEntry[]>}
 */
export async function getAll() {
  const server = await getAllFromServer();
  if (server) {
    await mirrorServerToLocal(server);
    return server;
  }
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

/**
 * Get homebrew entries by type (for compendium merge).
 * @param {string} type - monster | spell | item | race | class | background | feat | subclass
 * @returns {Promise<HomebrewEntry[]>}
 */
export async function getByType(type) {
  const server = await getAllFromServer(type);
  if (server) {
    await mirrorServerToLocal(server);
    return server;
  }
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const index = tx.objectStore(STORE).index('type');
      const req = index.getAll(type);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return [];
  }
}

/**
 * Get a single entry by id.
 * @param {string} id
 * @returns {Promise<HomebrewEntry|null>}
 */
export async function get(id) {
  const server = await getFromServer(id);
  if (server) return server;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(id);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch (err) {
    return null;
  }
}

/**
 * Save (create or update) an entry. If entry.id is missing, generate one and set created_at/updated_at.
 * @param {Partial<HomebrewEntry> & { name: string, type: string, data: object }} entry
 * @returns {Promise<HomebrewEntry>}
 */
export async function put(entry) {
  const now = new Date().toISOString();
  const normalizedType = String(entry.type || '').toLowerCase();
  const full = {
    id: entry.id || generateId(),
    name: entry.name,
    type: normalizedType,
    data: normalizeStructuredHomebrewData(normalizedType, entry.data || {}),
    tags: Array.isArray(entry.tags) ? entry.tags : [],
    source: entry.source ?? 'custom',
    version: entry.version ?? 1,
    visibility: entry.visibility ?? 'private',
    campaign_id: entry.campaign_id ?? null,
    created_at: entry.created_at || now,
    updated_at: now,
  };
  const server = await putToServer(full);
  if (server) {
    await putLocal(server);
    return server;
  }
  await putLocal(full);
  return full;
}

/**
 * Delete an entry by id.
 * @param {string} id
 * @returns {Promise<void>}
 */
export async function remove(id) {
  const removedOnServer = await removeFromServer(id);
  if (removedOnServer) {
    await removeLocal(id);
    return;
  }
  await removeLocal(id);
}

export async function createShareCode(id, options = {}) {
  if (!canUseServer()) throw new Error('Sign in and go online to create share codes.');
  return apiClient.createHomebrewShareCode(id, options);
}

export async function getShareCodes(id) {
  if (!canUseServer()) return [];
  return apiClient.getHomebrewShareCodes(id);
}

export async function redeemShareCode(code) {
  if (!canUseServer()) throw new Error('Sign in and go online to redeem share codes.');
  const result = await apiClient.redeemHomebrewShareCode(code);
  if (result?.linked_entry) await putLocal(result.linked_entry);
  return result;
}

function canUseServer() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
  if (typeof localStorage === 'undefined') return false;
  return !!localStorage.getItem('auth_token');
}

async function getAllFromServer(type = null) {
  if (!canUseServer()) return null;
  await migrateLocalToServerOnce();
  try {
    return await apiClient.getHomebrew(type);
  } catch (err) {
    return null;
  }
}

async function getFromServer(id) {
  if (!canUseServer()) return null;
  try {
    return await apiClient.getHomebrewEntry(id);
  } catch (_) {
    return null;
  }
}

async function putToServer(entry) {
  if (!canUseServer()) return null;
  try {
    if (entry.id && /^\d+$/.test(String(entry.id))) {
      return await apiClient.updateHomebrew(entry.id, entry);
    }
    return await apiClient.createHomebrew(entry);
  } catch (_) {
    return null;
  }
}

async function removeFromServer(id) {
  if (!canUseServer()) return false;
  try {
    await apiClient.deleteHomebrew(id);
    return true;
  } catch (_) {
    return false;
  }
}

async function mirrorServerToLocal(entries) {
  if (!Array.isArray(entries)) return;
  for (const entry of entries) {
    await putLocal(normalizeLegacyHomebrewEntry(entry));
  }
}

async function migrateLocalToServerOnce() {
  if (migrationAttempted) return;
  migrationAttempted = true;
  try {
    const local = await getAllLocal();
    if (!Array.isArray(local) || local.length === 0) return;
    const server = await apiClient.getHomebrew();
    const known = new Set((server || []).map((e) => `${String(e.type).toLowerCase()}::${String(e.name).toLowerCase()}`));
    for (const entry of local) {
      const normalizedEntry = normalizeLegacyHomebrewEntry(entry);
      const key = `${String(normalizedEntry.type).toLowerCase()}::${String(normalizedEntry.name).toLowerCase()}`;
      if (known.has(key)) continue;
      try {
        const created = await apiClient.createHomebrew({
          name: normalizedEntry.name,
          type: normalizedEntry.type,
          data: normalizedEntry.data,
          tags: normalizedEntry.tags || [],
          source: normalizedEntry.source || 'local-migrated',
          visibility: normalizedEntry.visibility || 'private',
        });
        if (created) {
          known.add(key);
          await putLocal(created);
          const oldId = normalizedEntry.id != null ? String(normalizedEntry.id) : '';
          const newId = created.id != null ? String(created.id) : '';
          if (oldId && newId && oldId !== newId && !/^\d+$/.test(oldId)) {
            try {
              await removeLocal(oldId);
            } catch (_) {
              // non-fatal
            }
          }
        }
      } catch (_) {
        // keep local copy; fail silently
      }
    }
  } catch (_) {
    // ignore
  }
}

async function getAllLocal() {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch (_) {
    return [];
  }
}

async function putLocal(entry) {
  const normalized = normalizeLegacyHomebrewEntry(entry);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(normalized);
    req.onsuccess = () => resolve(normalized);
    req.onerror = () => reject(req.error);
  });
}

async function removeLocal(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}

/**
 * Run after user-data online/login sync so local-only homebrew rows migrate to the server
 * without waiting for a compendium read. Safe to call repeatedly (migration is once per page load).
 */
export async function syncHomebrewAfterUserDataOnline() {
  await migrateLocalToServerOnce();
}
